// Reusable CSS classes and helpers for form components across the application
// Ensures consistent styling and makes it easy to update styles globally

import { Fragment, createElement } from "react";
import type { ReactNode } from "react";

// Input field base classes (text, email, password, etc)
export const INPUT_CLASSES =
  "mt-2 w-full rounded-2xl border border-blue-900/15 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-300/60";

// Input field with error state
export const INPUT_ERROR_CLASS = "border-red-500";

// Disabled input state
export const INPUT_DISABLED_CLASS = "opacity-60 cursor-not-allowed";

// Error message text
export const ERROR_TEXT_CLASS = "mt-1 text-xs text-red-600";

// Field wrapper (container for label + input + error)
export const FIELD_WRAPPER_CLASS = "space-y-2";

// Label text
export const LABEL_CLASS = "text-sm font-medium text-blue-950";

// Required field indicator (red asterisk)
export const REQUIRED_ASTERISK_CLASS = "text-red-600";

// Form section container
export const FORM_SECTION_CLASS =
  "rounded-3xl border border-blue-900/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(235,243,252,0.99))] p-6 shadow-[0_18px_45px_rgba(15,23,42,0.08)] sm:p-8";

// Form title
export const FORM_TITLE_CLASS = "mt-3 text-3xl font-semibold tracking-tight text-blue-950 sm:text-4xl";

// Form subtitle/intro
export const FORM_INTRO_CLASS = "mt-4 max-w-3xl text-base leading-7 text-slate-700 sm:text-lg";

// Submit button base classes
export const BUTTON_BASE_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-colors";

// Submit button hover state
export const BUTTON_HOVER_CLASS = "hover:bg-blue-700";

// Submit button disabled state
export const BUTTON_DISABLED_CLASS = "disabled:opacity-50 disabled:cursor-not-allowed";

// Success message box
export const SUCCESS_BOX_CLASS = "rounded-2xl border border-green-200 bg-green-50 p-4 space-y-3";

// Error message box
export const ERROR_BOX_CLASS = "rounded-2xl border border-red-200 bg-red-50 p-4";

// Warning message box
export const WARNING_BOX_CLASS = "rounded-2xl border border-yellow-200 bg-yellow-50 p-4";

// Status message text (inside success/error/warning boxes)
export const SUCCESS_TEXT_CLASS = "text-sm text-green-800";
export const ERROR_STATUS_TEXT_CLASS = "text-sm text-red-800";
export const WARNING_TEXT_CLASS = "text-sm text-yellow-800";

// Textarea field base classes
export const TEXTAREA_CLASSES =
  "mt-2 w-full rounded-2xl border border-blue-900/15 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-300/60";

// Checkbox field classes
export const CHECKBOX_CLASSES =
  "h-5 w-5 rounded border-blue-900/15 text-blue-600 focus:ring-2 focus:ring-blue-300/60";

// Helper text (below input, for additional info)
export const HELPER_TEXT_CLASS = "mt-2 text-xs text-slate-600";

// Combine classes safely (ignores empty strings)
export function combineClasses(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(" ");
}

// Apply error styling to input classes
export function getInputClassesWithError(hasError: boolean, isDisabled: boolean = false): string {
  let classes = INPUT_CLASSES;
  if (hasError) classes += ` ${INPUT_ERROR_CLASS}`;
  if (isDisabled) classes += ` ${INPUT_DISABLED_CLASS}`;
  return classes;
}

// Apply error styling to textarea classes
export function getTextareaClassesWithError(hasError: boolean, isDisabled: boolean = false): string {
  let classes = TEXTAREA_CLASSES;
  if (hasError) classes += ` ${INPUT_ERROR_CLASS}`;
  if (isDisabled) classes += ` ${INPUT_DISABLED_CLASS}`;
  return classes;
}

// Get button classes with states
export function getButtonClasses(): string {
  return `${BUTTON_BASE_CLASS} ${BUTTON_HOVER_CLASS} ${BUTTON_DISABLED_CLASS}`;
}

// Render a required-field label with the shared red asterisk style.
export function renderRequiredLabel(labelText: string, position: "start" | "end" = "end"): ReactNode {
  if (position === "start") {
    return createElement(
      Fragment,
      null,
      createElement("span", { className: "text-red-600" }, "* "),
      labelText,
    );
  }

  return createElement(
    Fragment,
    null,
    labelText,
    createElement("span", { className: "text-red-600" }, " *"),
  );
}
